'use client';

import {
  ChangeEvent,
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {cn} from '../lib/utils';
import {Input} from './input';

/**
 * Component that allows the user to edit a string.
 *
 * The editing mode can be controlled (the mode is managed by the parent component)
 * or uncontrolled (managed by the component itself).
 *
 * Controlled mode example:
 * ```
 *    const [text, setText] = useState('');
 *    const [isEditing, setEditing] = useState(false);
 *    ...
 *    <EditableText
 *       value={text}
 *       onChange={setText}
 *       isEditing={isEditing}
 *       onEditingChange={setEditing}
 *    />
 * ```
 *
 * Uncontrolled mode example:
 * ```
 *    const [text, setText] = useState('');
 *    ...
 *    <EditableText
 *      value={text}
 *      onChange={setText}
 *      defaultEditing={false}
 *    />
 * ```
 */

export const EditableText: FC<{
  className?: string;
  style?: CSSProperties;
  isReadOnly?: boolean;
  editTrigger?: 'click' | 'doubleClick';
  value: string;
  placeholder?: string;
  onChange: (text: string) => void;
  /** Called on each keystroke while editing. */
  onInputChange?: (text: string) => void;
  autoFocus?: boolean;
  selectOnFocus?: boolean;
  /** When false, the input is removed from tab order while not editing. */
  allowTabFocusWhenNotEditing?: boolean;

  /**
   * The editing state when it is initially rendered. Use when you do not need to control its editing state
   * in the parent component.
   **/
  defaultEditing?: boolean;

  /**
   * The controlled editing state of the component. Must be used in conjunction with onEditingChange.
   **/
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}> = ({
  className,
  style,
  isReadOnly = false,
  defaultEditing = false,
  editTrigger = 'click',
  isEditing,
  placeholder,
  value,
  onChange,
  onInputChange,
  onEditingChange,
  autoFocus,
  selectOnFocus = false,
  allowTabFocusWhenNotEditing = true,
}) => {
  const [isInternalEditing, setInternalIsEditing] = useState(defaultEditing);
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState(value);
  const internalValueRef = useRef(internalValue);
  const valueRef = useRef(value);
  const skipBlurHandlerRef = useRef(false);

  // Keep valueRef in sync
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    internalValueRef.current = internalValue;
  }, [internalValue]);

  // Keep internalValue in sync with value prop
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (value !== internalValueRef.current) {
      setInternalValue(value);
    }
  }, [value]);

  // Keep internal editing state in sync with controlled isEditing prop
  // and focus the input when editing is enabled
  useEffect(() => {
    if (isEditing !== undefined && isEditing !== isInternalEditing) {
      setInternalIsEditing(Boolean(isEditing));
      if (isEditing) {
        // When enabling editing from a dropdown menu, there will be a blur event when the dropdown closes,
        // so we need to wait a bit before making sure the input is focused and selected.
        const timeoutId = setTimeout(() => {
          inputRef.current?.focus();
          if (selectOnFocus) {
            inputRef.current?.select();
          }
        }, 200);
        return () => clearTimeout(timeoutId);
      }
    }
    return undefined;
  }, [isEditing, isInternalEditing, selectOnFocus]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSetValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isReadOnly || !isInternalEditing) {
        return;
      }
      const nextValue = e.target.value;
      onInputChange?.(nextValue);
      return setInternalValue(nextValue);
    },
    [isInternalEditing, isReadOnly, onInputChange],
  );

  const handleSetEditing = useCallback(
    (nextIsEditing: boolean) => {
      if (isReadOnly) {
        return;
      }
      setInternalIsEditing(nextIsEditing);
      onEditingChange?.(nextIsEditing);
    },
    [isReadOnly, onEditingChange],
  );

  const handleBlur = useCallback(() => {
    // Skip if blur was triggered by Enter/Escape key
    if (skipBlurHandlerRef.current) {
      skipBlurHandlerRef.current = false;
      return;
    }

    const trimmedValue = internalValueRef.current?.trim();
    onChange(trimmedValue);

    // After onChange, sync internal value back to the prop
    // If parent rejected the change, value prop stays the same and we reset
    // If parent accepted the change, value prop updates and we sync to it
    setInternalValue(valueRef.current);
    internalValueRef.current = valueRef.current;

    handleSetEditing(false);
  }, [handleSetEditing, onChange]);

  const handleClick = useCallback(() => {
    if (!isInternalEditing) {
      inputRef.current?.select();
      handleSetEditing(true);
    }
  }, [isInternalEditing, handleSetEditing]);

  const handleFocus = useCallback(() => {
    if (selectOnFocus && isInternalEditing) {
      inputRef.current?.select();
    }
  }, [isInternalEditing, selectOnFocus]);

  useEffect(() => {
    if (!isInternalEditing) return;
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        inputRef.current?.focus();
        if (selectOnFocus) {
          inputRef.current?.select();
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isInternalEditing, selectOnFocus]);

  // Add keydown event listener to handle enter key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          // Reset the internal value to the prop value
          setInternalValue(valueRef.current);
          internalValueRef.current = valueRef.current;
          handleSetEditing(false);
          skipBlurHandlerRef.current = true;
          inputRef.current?.blur();
          break;
        case 'Enter': {
          const trimmedValue = internalValueRef.current.trim();
          onChange(trimmedValue);

          // After onChange, sync internal value back to the prop
          // If parent rejected the change, value prop stays the same and we reset
          // If parent accepted the change, value prop updates and we sync to it
          setInternalValue(valueRef.current);
          internalValueRef.current = valueRef.current;

          handleSetEditing(false);
          skipBlurHandlerRef.current = true;
          inputRef.current?.blur();
          break;
        }
      }
    };
    if (isInternalEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isInternalEditing, onChange, handleSetEditing]);

  return (
    <Input
      ref={inputRef}
      className={cn(
        'w-full rounded-sm border-transparent px-1 py-0 disabled:opacity-1',
        'focus:outline-hidden disabled:cursor-text',
        {
          'focus:border-blue-500 focus:ring-blue-500': isInternalEditing,
          'bg-transparent select-none': !isInternalEditing,
          truncate: !isInternalEditing,
        },
        className,
      )}
      style={{
        ...(style ?? {}),
        caretColor: isInternalEditing ? style?.caretColor : 'transparent',
      }}
      value={internalValue}
      tabIndex={isInternalEditing || allowTabFocusWhenNotEditing ? 0 : -1}
      autoFocus={autoFocus}
      onChange={handleSetValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={isReadOnly}
      onClick={editTrigger === 'click' ? handleClick : undefined}
      onDoubleClick={editTrigger === 'doubleClick' ? handleClick : undefined}
      placeholder={
        !isInternalEditing ? (placeholder ?? 'Click to edit') : undefined
      }
    />
  );
};
